import { canDelete, type Board, type LifecycleAction, type Org, type Project } from '../../../lib/api';

/**
 * One action model for every dashboard surface (P16-4 Ruling 2): the ⋯ menu, the right-click menu, the bulk bar,
 * the keyboard and ⌘K all read these lists, so the rights are written once. They mirror the SQL rules: trash for
 * admins or the creator, nothing but opening for externals (spec §9.2), nothing that writes in an archived place.
 */
export type BoardActionId =
  | 'open'
  | 'openNewTab'
  | 'rename'
  | 'duplicate'
  | 'move'
  | 'favorite'
  | 'share'
  | 'saveTemplate'
  | 'archive'
  | 'unarchive'
  | 'trash';
export type ProjectActionId = 'open' | 'rename' | 'share' | 'archive' | 'unarchive' | 'trash';
export type BulkActionId = 'move' | 'duplicate' | 'favorite' | 'archive' | 'trash';
export type Denied = 'notMember' | 'notCreator' | 'archived' | 'notAdmin';
export type ActionState<T extends string> = { id: T; allowed: true } | { id: T; allowed: false; reason: Denied };

export type BoardContext = { org: Org | null; userId: string; favorite: boolean; canCopy: boolean; projectArchived: boolean };

const ok = <T extends string>(id: T): ActionState<T> => ({ id, allowed: true });
const no = <T extends string>(id: T, reason: Denied): ActionState<T> => ({ id, allowed: false, reason });
const unless = <T extends string>(id: T, reason: Denied | null): ActionState<T> => (reason ? no(id, reason) : ok(id));

export function boardActions(board: Board, ctx: BoardContext): ActionState<BoardActionId>[] {
  if (board.deletedAt) return [ok('open')];
  const member = ctx.org && ctx.org.id === board.orgId ? ctx.org : null;
  if (!member) return [ok('open'), ok('openNewTab'), ok('favorite')];
  const frozen: Denied | null = ctx.projectArchived ? 'archived' : null;
  const writable: Denied | null = frozen ?? (board.archivedAt ? 'archived' : null);
  const list: ActionState<BoardActionId>[] = [ok('open'), ok('openNewTab'), unless('rename', writable)];
  if (ctx.canCopy) list.push(ok('duplicate'));
  list.push(unless('move', writable), ok('favorite'), ok('share'));
  if (ctx.canCopy && !board.isTemplate) list.push(unless('saveTemplate', writable));
  list.push(board.archivedAt ? unless('unarchive', frozen) : unless('archive', frozen));
  list.push(unless('trash', canDelete(member.role, board.createdBy, ctx.userId) ? null : 'notCreator'));
  return list;
}

const BULK: readonly BulkActionId[] = ['move', 'duplicate', 'favorite', 'archive', 'trash'];

/** For each bulk action, the selected boards it may act on (the bar says "2 of 4" when that is not all of them). */
export function bulkActions(boards: readonly Board[], ctx: Omit<BoardContext, 'favorite'>): { id: BulkActionId; allowedIds: string[] }[] {
  const allowed = new Map(boards.map((b) => [b.id, boardActions(b, { ...ctx, favorite: false })]));
  return BULK.map((id) => ({
    id,
    allowedIds: boards.filter((b) => allowed.get(b.id)!.some((a) => a.id === id && a.allowed)).map((b) => b.id),
  }));
}

export function projectActions(project: Project, ctx: { org: Org | null; userId: string }): ActionState<ProjectActionId>[] {
  if (project.deletedAt) return [ok('open')];
  const member = ctx.org && ctx.org.id === project.orgId ? ctx.org : null;
  if (!member) return [ok('open')];
  const archived: Denied | null = project.archivedAt ? 'archived' : null;
  return [
    ok('open'),
    unless('rename', archived),
    ok('share'),
    project.archivedAt ? ok('unarchive') : ok('archive'),
    unless('trash', canDelete(member.role, project.createdBy, ctx.userId) ? null : 'notCreator'),
  ];
}

/** What "Undo" runs (P16-4 Ruling 3): the inverse operation, from the board as it was before the action. */
export type Inverse =
  | { kind: 'move'; boardId: string; projectId: string; folderId: string | null }
  | { kind: 'lifecycle'; boardId: string; action: LifecycleAction }
  | { kind: 'projectLifecycle'; projectId: string; action: LifecycleAction }
  | { kind: 'favorite'; board: Board; on: boolean }
  | { kind: 'rename'; boardId: string; title: string };

const LIFECYCLE_INVERSE: Partial<Record<BoardActionId | ProjectActionId, LifecycleAction>> = { archive: 'unarchive', unarchive: 'archive', trash: 'restore' };

export function inverseOf(action: BoardActionId, before: Board, input?: { favorite?: boolean }): Inverse | null {
  if (action === 'move') return { kind: 'move', boardId: before.id, projectId: before.projectId, folderId: before.folderId };
  if (action === 'rename') return { kind: 'rename', boardId: before.id, title: before.title };
  if (action === 'favorite') return { kind: 'favorite', board: before, on: !input?.favorite };
  const lifecycle = LIFECYCLE_INVERSE[action];
  return lifecycle ? { kind: 'lifecycle', boardId: before.id, action: lifecycle } : null;
}

export function projectInverseOf(action: ProjectActionId, before: Project): Inverse | null {
  const lifecycle = LIFECYCLE_INVERSE[action];
  return lifecycle ? { kind: 'projectLifecycle', projectId: before.id, action: lifecycle } : null;
}
